import { useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import _pick from 'lodash/pick';

import axios from './axios';
import { OPPORTUNITIES_SEARCH_QUERY_KEYS } from 'helpers/opportunities';
import { vendorSchema } from 'constants/schemas';
import { VENDORS_ME, VENDORS_ME_TAXONOMYCODES } from 'constants/apis';
import { ToastContext } from 'contexts';

const defaultVendorBusinessInfoFieldTranslations = [
  { name: 'vendorContactOrganizationName', translation: 'welcome.business_name_label' },
  { name: 'vendorContactFirstName', translation: 'welcome.vendor_contact_first_name_label' },
  { name: 'vendorContactLastName', translation: 'welcome.vendor_contact_last_name_label' },
  { name: 'vendorContactEmail', translation: 'welcome.vendor_contact_email_label' },
  { name: 'businessDescription', translation: 'welcome.business_description.label' },
  { name: 'phoneNumber', translation: 'welcome.phone_number_label' },
  { name: 'addressLine1', translation: 'welcome.address_line_1_label' },
  { name: 'addressLine2', translation: 'welcome.address_line_2_label' },
  { name: 'addressLine3', translation: 'welcome.address_line_3_label' },
  { name: 'addressLine4', translation: 'welcome.address_line_4_label' },
  { name: 'city', translation: 'welcome.city_label' },
  { name: 'region', translation: 'welcome.region_label' },
  { name: 'country', translation: 'welcome.country_label' },
  { name: 'postalCode', translation: 'welcome.postal_or_zip_code_label' },
  { name: 'role', translation: 'welcome.business_info.your_role' },
  { name: 'numEmployees', translation: 'welcome.business_info.num_employees' },
];

const defaultVendorBusinessInfoFields = defaultVendorBusinessInfoFieldTranslations.map(
  ({ name }) => name
);

export const pickVendorBusinessInfoFields = (vendor, fields = defaultVendorBusinessInfoFields) =>
  _pick(vendor, fields);

/**
 * Creates a new `Joi` schema and applies translations to the error messages.
 *
 * @param {Array<object>} translationFields An array of input field names that also contain the translation keys.
 * @param {import('joi').ObjectSchema} schema The `Joi` object schema to validate against.
 * @returns A `Joi` schema with translated error messages.
 */
export const useTranslatedVendorSchema = (
  translationFields = defaultVendorBusinessInfoFieldTranslations,
  schema = vendorSchema
) => {
  const { t } = useTranslation();

  return useMemo(() => {
    let newSchema = schema;
    for (const { name, translation } of translationFields) {
      newSchema = newSchema.fork([name], s =>
        s.messages({
          'string.empty': t('form_errors.required', {
            field: t(translation),
            interpolation: { escapeValue: false },
          }),
          'string.max': t('form_errors.max_length', {
            field: t(translation),
            interpolation: { escapeValue: false },
          }),
        })
      );
    }

    return newSchema;
  }, [t, translationFields, schema]);
};

const VENDORS_ME_QUERY_KEY = ['vendors', 'me'];
const VENDOR_TAXONOMY_CODE_QUERY_KEY = ['vendors', 'me', 'taxonomyCodes'];

export const useVendor = () => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  return useQuery(VENDORS_ME_QUERY_KEY, () => axios.get(VENDORS_ME).then(({ data }) => data), {
    onError: () => triggerToast({ message: t('generic_error') }),
    staleTime: 60 * 60 * 1000,
  });
};
export const useMutateVendor = () => {
  const queryClient = useQueryClient();
  return useMutation(({ payload }) => axios.patch(VENDORS_ME, payload), {
    onSuccess: (data, variables) => {
      queryClient.setQueryData(VENDORS_ME_QUERY_KEY, prev => ({ ...prev, ...variables.payload }));
      if (variables.resetRecommendedOpportunities) {
        queryClient.resetQueries(OPPORTUNITIES_SEARCH_QUERY_KEYS.type('recommended'), {
          exact: false,
        });
      }
    },
  });
};

export const useVendorTaxonomyCodes = () => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  return useQuery(
    VENDOR_TAXONOMY_CODE_QUERY_KEY,
    () => axios.get(VENDORS_ME_TAXONOMYCODES).then(({ data }) => data),
    {
      onError: () => triggerToast({ message: t('generic_error') }),
      staleTime: 5 * 60 * 1000,
    }
  );
};
export const useMutateVendorTaxonomyCodes = () => {
  const queryClient = useQueryClient();
  return useMutation(
    taxonomyCodes =>
      axios.put(
        VENDORS_ME_TAXONOMYCODES,
        taxonomyCodes.map(code => code.taxonomyCodeID)
      ),
    {
      onSuccess: (data, newCodes) => {
        queryClient.setQueryData(VENDOR_TAXONOMY_CODE_QUERY_KEY, () => newCodes);
        queryClient.resetQueries(OPPORTUNITIES_SEARCH_QUERY_KEYS.type('recommended'), {
          exact: false,
        });
      },
    }
  );
};
